import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';
import { Field, controlClasses, describedBy } from './Field';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Optional grouping. Options sharing a group render under one `<optgroup>`. */
  group?: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label: ReactNode;
  options: SelectOption[];
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  /** Rendered as a disabled first option. Omit for a select that must have a value. */
  placeholder?: string;
  containerClassName?: string;
}

/**
 * A native `<select>`, deliberately.
 *
 * A custom listbox is more work to make accessible than it looks, and it loses the
 * platform behaviour people already know — type-ahead, mobile pickers, keyboard
 * paging. When a screen genuinely needs search-as-you-type over hundreds of matters,
 * that is a Combobox and a separate component, not a widened Select.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    options,
    hint,
    error,
    hideLabel,
    placeholder,
    containerClassName,
    className,
    id: providedId,
    required,
    ...props
  },
  ref,
) {
  const id = useFieldId(providedId);

  const groups = options.reduce<Map<string | undefined, SelectOption[]>>((acc, option) => {
    const key = option.group;
    const bucket = acc.get(key);
    if (bucket) bucket.push(option);
    else acc.set(key, [option]);
    return acc;
  }, new Map());

  const renderOption = (option: SelectOption) => (
    <option key={option.value} value={option.value} disabled={option.disabled}>
      {option.label}
    </option>
  );

  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      error={error}
      required={required}
      hideLabel={hideLabel}
      className={containerClassName}
    >
      <div className="relative">
        <select
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          className={cn(controlClasses(Boolean(error)), 'h-9 appearance-none pr-9', className)}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {[...groups.entries()].map(([group, groupOptions]) =>
            group ? (
              <optgroup key={group} label={group}>
                {groupOptions.map(renderOption)}
              </optgroup>
            ) : (
              groupOptions.map(renderOption)
            ),
          )}
        </select>
        <svg
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </Field>
  );
});
